import { Button } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import OpenInBrowserIcon from '@material-ui/icons/OpenInBrowser';
import { PurchaseFromInventoryItemForm } from 'app/main/financial/purchases/forms/register/PurchaseFromInventoryItemForm';
import { PageBreadcrumbDivider } from 'framework/components/breadcrumbs/PageBreadcrumbDivider';
import { PageBreadcrumbTitle } from 'framework/components/breadcrumbs/PageBreadcrumbTitle';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { CardX } from 'framework/components/cards/CardX';
import { StatusIcon } from 'framework/components/icons/StatusIcon';
import { CaptionVerticalLabelledPropertyX } from 'framework/components/labelledProperty/CaptionVerticalLabelledPropertyX';
import { PageReloadButtonX } from 'framework/components/layout/PageReloadButtonX';
import { PageX } from 'framework/components/layout/PageX';
import { PageXHeaderActions } from 'framework/components/layout/PageXHeaderActions';
import { Typography16pxBold } from 'framework/components/typography/Typography16pxBold';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { IRouteParamsWithId } from 'framework/router/IRouteParamsWithId';
import { formatDate } from 'framework/utils/date/formatDate';
import { getNoahDeviceDtoById_handle, INoahDeviceDto, patientsQuery_details } from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { InventoryItemRegisterAsSoldForm } from '../../inventoryItems/forms/InventoryItemRegisterAsSoldForm';
import { InventoryManagementPageBreadcrumb } from '../../InventoryManagementPageBreadcrumb';
import { calcInventoryItemDetailRoute } from '../../routes';
import { ImportDeviceForm } from './forms/ImportDeviceForm';
import { OpenInBrowser } from '@material-ui/icons';
import { calcPatientDetailRoute } from 'app/main/patients/calcPatientDetailRoute';
import { PatientDetailRenewalInfoCardContent } from 'app/main/patients/detailX/personalData/PatientDetailRenewalInfoCardContent';

const ImportNoahDevicePage = () => {
	const { id } = useParams<IRouteParamsWithId>();
	const [data, reloadDetail] = useApiEffect(getNoahDeviceDtoById_handle, id);

	if (!data) {
		return <></>;
	}
	return (
		<InnerPage
			data={data}
			reload={reloadDetail}
		/>
	);
};

export default ImportNoahDevicePage;

type InnerPageProps = {
	data: INoahDeviceDto;
	reload: VoidFunction;
};

const InnerPage = ({ data, reload }: InnerPageProps) => {
	const strings = useLocalization();
	const { open, confirm, cancel } = useDialogsContext(reload);
	const { push } = useHistory();

	const onImport = () => {
		open(
			<ImportDeviceForm
				id={data.data.id}
				confirm={confirm}
				cancel={cancel}
			/>
		);
	};

	const onRegisterSale = () => {
		if (!data.inventoryItem) {
			return;
		}
		open(
			<InventoryItemRegisterAsSoldForm
				open
				patient={data.data.lastUsage?.patient}
				inventoryItemId={data.inventoryItem.id}
				confirm={confirm}
				cancel={cancel}
			/>
		);
	};

	const onRegisterPurchase = () => {
		if (!data.inventoryItem) {
			return;
		}
		open(
			<PurchaseFromInventoryItemForm
				open
				inventoryItemId={data.inventoryItem.id}
				confirm={confirm}
				cancel={cancel}
			/>
		);
	};

	return (
		<PageX
			stickyHeader={
				<div
					className='df-row-ac'
					style={{ paddingBottom: 16 }}>
					<InventoryManagementPageBreadcrumb />
					<PageBreadcrumbDivider />
					<PageBreadcrumbTitle title={`Import Noah device`} />
					<PageBreadcrumbDivider />
					<div className='fg1'></div>
					<PageXHeaderActions>
						<PageReloadButtonX reload={reload} />
					</PageXHeaderActions>
				</div>
			}>
			<div
				className='df-col gap-16'
				style={{
					padding: 16,
					// maxWidth: 1280,
				}}>
				<CardX>
					<Typography16pxBold>{strings.details}</Typography16pxBold>
					<CaptionVerticalLabelledPropertyX label={strings.serialNumber}>{data.data.serialNumber}</CaptionVerticalLabelledPropertyX>
					<CaptionVerticalLabelledPropertyX label={strings.brandAndModel}>{data.data.manufacturerCommaInstrumentTypeName}</CaptionVerticalLabelledPropertyX>
					<CaptionVerticalLabelledPropertyX label={strings.lastReadoutOnPatient}>
						{data.data.lastUsage?.patient && (
							<TextLinkButton
								startIcon={<OpenInBrowser />}
								onClick={() => push(calcPatientDetailRoute(data.data.lastUsage.patientId))}>
								{data.data.lastUsage.patientIdentifier}
							</TextLinkButton>
						)}
						{Boolean(data.data.lastUsage?.patient) === false && strings.notApplicableAbbreviation}
					</CaptionVerticalLabelledPropertyX>
					<CaptionVerticalLabelledPropertyX label={strings.lastUsageDate}>{formatDate(data.data.lastUsageDate, 'only-date')}</CaptionVerticalLabelledPropertyX>
				</CardX>
				<CardX className='df-col gap-8'>
					<div className='df-row-ac gap-8'>
						<StatusIcon
							status={data.hasInventoryItem ? 'success' : 'error'}
							style={{ marginRight: 4 }}
							fontSize='small'
						/>
						<Typography16pxBold>{strings.addDeviceToInventory}</Typography16pxBold>
					</div>
					{data.inventoryItem && (
						<TextLinkButton
							startIcon={<OpenInBrowserIcon />}
							onClick={() => push(calcInventoryItemDetailRoute(data.inventoryItem.id))}>
							{strings.viewInventoryItem}
						</TextLinkButton>
					)}
					<div>
						<Button
							startIcon={<LinkIcon />}
							disabled={data.hasInventoryItem === true}
							onClick={onImport}
							color='primary'
							size='small'
							variant='outlined'>
							{strings.addToInventory}
						</Button>
					</div>
				</CardX>
				<CardX className='df-col gap-8'>
					<div className='df-row-ac gap-8'>
						<StatusIcon
							status={data.inventoryItem && data.inventoryItem.isSold ? 'success' : 'error'}
							style={{ marginRight: 4 }}
							fontSize='small'
						/>
						<Typography16pxBold>{strings.registerAsSold}</Typography16pxBold>
					</div>
					<div>
						<Button
							startIcon={<LinkIcon />}
							disabled={data.canRegisterSale === false}
							onClick={onRegisterSale}
							color='primary'
							size='small'
							variant='outlined'>
							{strings.registerAsSold}
						</Button>
					</div>
				</CardX>
				<CardX className='df-col gap-8'>
					<div className='df-row-ac gap-8'>
						<StatusIcon
							status={data.inventoryItem && data.inventoryItem.isPurchased ? 'success' : 'error'}
							style={{ marginRight: 4 }}
							fontSize='small'
						/>
						<Typography16pxBold>{strings.registerHistoricalPurchase}</Typography16pxBold>
					</div>
					<div>
						<Button
							startIcon={<LinkIcon />}
							disabled={data.canRegisterPurchase === false}
							onClick={onRegisterPurchase}
							color='primary'
							size='small'
							variant='outlined'>
							{strings.registerHistoricalPurchase}
						</Button>
					</div>
				</CardX>
				{data.data.lastUsage.patient && <InnerPatientRenewalCard patientId={data.data.lastUsage.patient.id} />}
			</div>
		</PageX>
	);
};

type InnerPatientRenewalProps = {
	patientId: string;
};

const InnerPatientRenewalCard = ({ patientId }: InnerPatientRenewalProps) => {
	const [data, reloadDetail] = useApiEffect(patientsQuery_details, patientId);

	if (!data) {
		return <></>;
	}

	return (
		<CardX>
			<Typography16pxBold>{data.patient.lastNameCommaFirstNameBracketsNickName}</Typography16pxBold>
			<PatientDetailRenewalInfoCardContent
				item={data.patient}
				reload={reloadDetail}
			/>
		</CardX>
	);
};
